import { AppContext, SensorSelectionContext, appActions } from 'context';
import { useFileUpload } from 'hooks/use-file-upload';
import { useContext, useEffect, useRef, useState } from 'react';
import ysFixWebmDuration from 'fix-webm-duration';
import { SelfRecordingContext } from 'context/self-recording-context';
import { NotificationObject } from 'models/notificationObject';
import {
  AlbumAddContentTypes,
  NotificationType,
  RecordingState,
  SelfRecordingStatus,
} from 'common/enums';
import { selfRecordingActions } from 'context/self-recording-context/action-types';
import { captureException } from 'sentry';
import { SensorSelectionActions } from 'context/sensor-selection-context/action-types';

export const useSelfRecording = (isAudio: boolean = false) => {
  const { sensorSelection, updateSensorSelection } = useContext(
    SensorSelectionContext,
  );
  const [mediaRecorderState, setMediaRecorder] =
    useState<MediaRecorder | null>();
  const { state: selfRecordingState, dispatch: selfRecordingDispatch } =
    useContext(SelfRecordingContext);
  const chunks = useRef([] as any[]);
  const startedTimes = useRef<[number]>([0]);
  const endedTimes = useRef<[number]>([0]);
  const [countingTime, setCountingTime] = useState(false);
  const [mimeType, setMimeType] = useState<string>('video/webm');
  const { uploadS3File, response, errorWithId } = useFileUpload();
  const { dispatch } = useContext(AppContext);
  const amountOfClips = useRef(0);
  const { selectedQuestion, videosRecorded } = selfRecordingState;
  const videosRecordedRef = useRef(videosRecorded);

  useEffect(() => {
    videosRecordedRef.current = videosRecorded;
  }, [videosRecorded]);

  const uploadVideo = async (requestId: string, chunksToUpload: Blob[]) => {
    const recordedBlob = new Blob(chunksToUpload, {
      type: mimeType,
    });

    const fixedBlob = await ysFixWebmDuration(
      recordedBlob,
      endedTimes.current[endedTimes.current.length - 1]! -
        startedTimes.current[startedTimes.current.length - 1]!,
      { logger: false },
    );

    const fileToUpload = new File(
      [fixedBlob],
      selectedQuestion ? selectedQuestion?.name : 'Custom recording',
      {
        type: mimeType,
      },
    );
    selfRecordingDispatch({
      type: selfRecordingActions.updateInformation,
      updateInformation: {
        requestId,
        file: fileToUpload,
      },
    });
    uploadS3File(fileToUpload, requestId);
  };

  const closeAllTracks = () => {
    selfRecordingState.mediaStream?.getTracks().forEach((track) => {
      track.stop();
    });
  };

  const retryFailedRequests = () => {
    selfRecordingState.videosRecorded.forEach((video) => {
      if (video.status === SelfRecordingStatus.error) {
        if (video.file) {
          selfRecordingDispatch({
            type: selfRecordingActions.updateInformation,
            updateInformation: {
              requestId: video.requestId,
              status: SelfRecordingStatus.processing,
            },
          });
          uploadS3File(video.file, video.requestId);
        } else {
          dispatch({
            type: appActions.NOTIFICATION,
            notification: new NotificationObject({
              show: true,
              title: 'Something went wrong',
              message: 'Please try again',
              type: NotificationType.Error,
            }),
          });
        }
      }
    });
  };

  useEffect(() => {
    if (errorWithId && errorWithId.requestId) {
      selfRecordingDispatch({
        type: selfRecordingActions.videoFailed,
        idWhoseVideoFailed: errorWithId.requestId,
      });
    }
  }, [errorWithId]);

  useEffect(() => {
    if (response && response.requestId) {
      selfRecordingDispatch({
        type: selfRecordingActions.addVideo,
        videoToAppend: {
          requestId: response.requestId,
          videoId: response.id,
          videoUrl: response.url,
          videoSignedId: response.signedId,
        },
      });
      selfRecordingDispatch({ type: selfRecordingActions.setReadyForNextStep });
    }
  }, [response]);

  const handleDataAvailable = (event: { data: Blob }) => {
    if (event.data.size > 0) {
      chunks.current.push(event.data);
    }
  };

  const handleStartRecording = () => {
    amountOfClips.current += 1;
    startedTimes.current.push(Date.now());
    selfRecordingDispatch({
      type: selfRecordingActions.newClip,
      videoToAppend: {
        requestId: amountOfClips.current.toString(),
        clip: selectedQuestion,
        videoId: 0,
        videoUrl: '',
        status: SelfRecordingStatus.processing,
        contentType: isAudio
          ? AlbumAddContentTypes.audio
          : AlbumAddContentTypes.video,
        isAudioOnly: isAudio,
        name: selectedQuestion?.name || 'Custom recording',
      },
    });
    selfRecordingDispatch({
      type: selfRecordingActions.setIsRecording,
      isRecording: true,
    });
    selfRecordingDispatch({
      type: selfRecordingActions.setRecordingState,
      recordingState: RecordingState.RECORDING,
    });
    setCountingTime(true);
  };

  const handlePauseRecording = () => {
    if (mediaRecorderState && mediaRecorderState.state === 'recording') {
      mediaRecorderState.pause();
    }
  };

  const handleResumeRecording = () => {
    if (mediaRecorderState && mediaRecorderState.state === 'paused') {
      mediaRecorderState.resume();
    }
  };

  const handleRestartRecording = () => {
    if (mediaRecorderState) {
      mediaRecorderState.onstop = null;
      mediaRecorderState.stop();

      chunks.current = [];

      startedTimes.current = [0];
      endedTimes.current = [0];

      selfRecordingDispatch({
        type: selfRecordingActions.deleteAllVideos,
      });

      selfRecordingDispatch({
        type: selfRecordingActions.setIsRecording,
        isRecording: false,
      });
      selfRecordingDispatch({
        type: selfRecordingActions.setRecordingState,
        recordingState: RecordingState.READY_TO_RECORD,
      });
      selfRecordingDispatch({
        type: selfRecordingActions.setClipNumber,
        clipNumber: 0,
      });

      setCountingTime(false);
      amountOfClips.current = 0;

      setMediaRecorder(null);
    }
  };

  const handleStopRecording = () => {
    endedTimes.current.push(Date.now());
    const currentChunks = [...chunks.current];
    chunks.current = [];
    selfRecordingDispatch({
      type: selfRecordingActions.setIsRecording,
      isRecording: false,
    });
    selfRecordingDispatch({
      type: selfRecordingActions.setRecordingState,
      recordingState: RecordingState.READY_TO_RECORD,
    });
    setCountingTime(false);
    uploadVideo(
      videosRecordedRef.current[videosRecordedRef.current.length - 1]
        ?.requestId,
      currentChunks,
    );
  };

  const optionsMediaRecorder = () => {
    let options = { mimeType: 'video/webm' } as {
      mimeType: string;
      videoBitsPerSecond?: number;
    };

    if (isAudio) {
      if (MediaRecorder.isTypeSupported('audio/webm; codecs=opus')) {
        options = { mimeType: 'audio/webm; codecs=opus' };
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        options = { mimeType: 'audio/webm' };
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        options = { mimeType: 'audio/mp4' };
      } else if (MediaRecorder.isTypeSupported('audio/ogg; codecs=opus')) {
        options = { mimeType: 'audio/ogg; codecs=opus' };
      } else {
        captureException(
          new Error('No suitable audio mimetype found for this device'),
        );
      }
    } else if (MediaRecorder.isTypeSupported('video/webm; codecs=vp9')) {
      options = { mimeType: 'video/webm; codecs=vp9' };
    } else if (MediaRecorder.isTypeSupported('video/webm')) {
      options = { mimeType: 'video/webm' };
    } else if (MediaRecorder.isTypeSupported('video/mp4')) {
      options = { mimeType: 'video/mp4', videoBitsPerSecond: 100000 };
    } else {
      captureException(
        new Error('No suitable video mimetype found for this device'),
      );
    }
    setMimeType(options.mimeType);
    return options;
  };

  const onSuccess = (stream: MediaStream) => {
    const options = optionsMediaRecorder();
    const mediaRecorder = new MediaRecorder(stream, options);
    mediaRecorder.ondataavailable = handleDataAvailable;
    mediaRecorder.onstop = handleStopRecording;
    mediaRecorder.onstart = handleStartRecording;
    mediaRecorder.onpause = () => {
      selfRecordingDispatch({
        type: selfRecordingActions.setRecordingState,
        recordingState: RecordingState.PAUSED,
      });
      setCountingTime(false);
    };
    mediaRecorder.onresume = () => {
      selfRecordingDispatch({
        type: selfRecordingActions.setRecordingState,
        recordingState: RecordingState.RECORDING,
      });
      setCountingTime(true);
    };
    setMediaRecorder(mediaRecorder);
    selfRecordingDispatch({
      type: selfRecordingActions.setMediaStream,
      mediaStream: stream,
    });
    return mediaRecorder;
  };

  const createMediaRecorder = async () => {
    const constraints = {
      audio: sensorSelection.microphone.enabled,
      video: !isAudio && sensorSelection.camera.enabled,
    };
    const promises = await navigator.mediaDevices.getUserMedia(constraints);

    if (promises) {
      const mediaRecorder = onSuccess(promises);
      return mediaRecorder;
    }
    dispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({
        show: true,
        title: isAudio
          ? 'Something went wrong with the microphone'
          : 'Something went wrong with the camera or microphone',
        message: 'Please try again',
        type: NotificationType.Error,
      }),
    });
    return null;
  };

  useEffect(() => {
    if (!isAudio) {
      updateSensorSelection({
        type: SensorSelectionActions.CAMERA_TOGGLED,
        enabled: true,
      });
    }
    updateSensorSelection({
      type: SensorSelectionActions.MICROPHONE_TOGGLED,
      enabled: true,
    });
  }, [isAudio, updateSensorSelection]);

  return {
    closeAllTracks,
    mediaRecorderState,
    countingTime,
    retryFailedRequests,
    createMediaRecorder,
    handlePauseRecording,
    handleResumeRecording,
    handleRestartRecording,
  };
};
